import { Button } from "@postdaily/ui/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@postdaily/ui/components/empty";
import { cn } from "@postdaily/ui/lib/utils";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import {
  parseAsArrayOf,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from "nuqs";
import { useEffect } from "react";
import { z } from "zod";

import { ClientContainer } from "@/calendar/components/client-container";
import {
  CalendarProvider,
  type ChannelSelection,
} from "@/calendar/contexts/calendar-context";
import type { TCalendarView } from "@/calendar/types";
import { useComposer } from "@/components/composer/composer-dialog-provider";
import { PageHeader } from "@/components/page-header";
import {
  approvalStatusesForBucket,
  isStatusBucket,
  type PublishLayout,
  STATUS_BUCKETS,
  type StatusBucket,
  statusesForBucket,
} from "@/components/publish/filters";
import {
  PublishFilterBar,
  PublishLayoutToggle,
} from "@/components/publish/publish-filter-bar";
import { PublishListView } from "@/components/publish/publish-list-view";
import { resolveDefaultTimezone } from "@/components/scheduling/schedule-time";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/(app)/calendar/$view")({
  component: RouteComponent,
});

const calendarViewSchema = z.enum(["day", "week", "month", "year", "agenda"]);

/**
 * The Publish hub. A single surface with a List ⇄ Calendar layout toggle and a
 * shared, URL-persisted filter bar (Buffer-simple status buckets + Tags +
 * Channels). The `$view` path param is the calendar sub-view (day/week/month/
 * year/agenda); the layout toggle and filters live in the URL search so they
 * persist across the toggle and sub-view navigation.
 */
function RouteComponent() {
  const { view } = Route.useParams();
  const navigate = Route.useNavigate();
  const { currentWorkspace } = useCurrentWorkspace();
  const { openComposer } = useComposer();
  const parsedView = calendarViewSchema.safeParse(view);

  const [filters, setFilters] = useQueryStates({
    channels: parseAsArrayOf(parseAsString).withDefault([]),
    layout: parseAsStringEnum<PublishLayout>(["list", "calendar"]).withDefault(
      "calendar",
    ),
    status: parseAsStringEnum<StatusBucket>([...STATUS_BUCKETS]).withDefault(
      "scheduled",
    ),
    tags: parseAsArrayOf(parseAsString).withDefault([]),
  });

  // An unknown `status` falls back to the default bucket, but the URL keeps
  // claiming the bad one — so a stale or mistyped link (`?status=draft`, when
  // the bucket is `drafts`) renders the Scheduled tab, usually empty, reading
  // as "you have no posts". Rewrite the URL to the bucket actually shown.
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get("status");

    if (raw && !isStatusBucket(raw)) {
      void setFilters({ status: null }, { history: "replace" });
    }
  }, [setFilters]);

  // The toggle reports a click on the already-active layout too.
  const changeLayout = (layout: PublishLayout) => {
    if (layout === filters.layout) {
      return;
    }

    track("publish_layout_changed", { layout });
    void setFilters({ layout });
  };

  if (!parsedView.success) {
    return <Navigate to="/calendar/$view" params={{ view: "week" }} replace />;
  }

  const calendarView = parsedView.data;
  const workspaceId = currentWorkspace?.id ?? "";
  const bucket = filters.status;
  const statuses = statusesForBucket(bucket);
  // Only the Approvals bucket narrows by approval state; every other bucket
  // leaves this unset so pending posts still show under Scheduled/Drafts too.
  const approvalStatuses = approvalStatusesForBucket(bucket);
  // Every time in the hub (calendar grid, list rows, day groups, the timezone
  // chip) renders in this zone: the workspace timezone, or the browser's when
  // the workspace still has the "UTC" placeholder.
  const displayTimezone = resolveDefaultTimezone(
    currentWorkspace?.defaultTimezone,
  );

  // nuqs channels: empty array = no filter. The filter bar speaks
  // ChannelSelection ("all" = no filter), so translate at the boundary.
  const channelValue: ChannelSelection =
    filters.channels.length > 0 ? filters.channels : "all";

  // Drafts have no scheduledAt, so they can never appear on the calendar grid.
  const showDraftsOnCalendarNotice =
    filters.layout === "calendar" && bucket === "drafts";
  // Calendar layout fills the viewport and scrolls inside the grid; list keeps
  // normal page scroll in the shell main.
  const fillViewport =
    filters.layout === "calendar" && !showDraftsOnCalendarNotice;

  return (
    <CalendarProvider
      timezone={displayTimezone}
      onDrillDown={(_date, view) =>
        void navigate({
          to: "/calendar/$view",
          params: { view },
          search: (prev) => prev,
        })
      }
    >
      <div
        className={cn(
          "flex flex-col gap-4",
          // Calendar layout fills the viewport and scrolls inside the grid; list keeps
          // normal page scroll in the shell main. Tighter gap than other pages — the
          // header + single filter row should leave max room for the grid.
          fillViewport && "min-h-0 flex-1 gap-3 overflow-y-auto",
        )}
        {...(fillViewport ? { "data-calendar-fill": "" } : {})}
      >
        <PageHeader
          size="lg"
          title="All Channels"
          description="Plan, schedule, and review posts across every connected channel."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <PublishLayoutToggle
                value={filters.layout}
                onChange={changeLayout}
              />
              <Button size="sm" onClick={() => openComposer()}>
                New post
              </Button>
            </div>
          }
        />

        <div className="shrink-0">
          <PublishFilterBar
            status={bucket}
            onStatusChange={(status) => void setFilters({ status })}
            channelValue={channelValue}
            onChannelChange={(next) =>
              void setFilters({ channels: next === "all" ? [] : next })
            }
            tagIds={filters.tags}
            onTagIdsChange={(tags) => void setFilters({ tags })}
            countAccountIds={filters.channels}
          />
        </div>

        {filters.layout === "list" ? (
          <PublishListView
            workspaceId={workspaceId}
            statuses={statuses}
            approvalStatuses={approvalStatuses}
            tagIds={filters.tags}
            accountIds={filters.channels}
            bucket={bucket}
            displayTimezone={displayTimezone}
          />
        ) : showDraftsOnCalendarNotice ? (
          <Empty className="rounded-xl border">
            <EmptyHeader>
              <EmptyTitle>Drafts aren’t scheduled</EmptyTitle>
              <EmptyDescription>
                Drafts have no scheduled time, so they don’t appear on the
                calendar. Switch to the List view to see them.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={() => changeLayout("list")}>
                Switch to List
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <ClientContainer
            view={calendarView}
            onSelectView={(next: TCalendarView) =>
              void navigate({
                to: "/calendar/$view",
                params: { view: next },
                search: (prev) => prev,
              })
            }
            showChannelFilter={false}
            accountIds={filters.channels}
            statuses={statuses}
            approvalStatuses={approvalStatuses}
            tagIds={filters.tags}
          />
        )}
      </div>
    </CalendarProvider>
  );
}
